/**
 * 008-reporting-dashboards T021 (FR-013 to FR-016). `movementValue`/`movementPct` are the API's own
 * figures (`Rounding.Percentage` on the backend's unrounded comparison) — never recomputed here.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import {
  PiArrowsClockwiseBold as UpdateIcon,
  PiCurrencyInrBold as PriceIcon,
  PiFunnelBold as FilterIcon,
  PiListChecksBold as RulesIcon,
  PiLockSimpleDuotone,
  PiStackBold as TierIcon,
  PiWarningDuotone,
} from "react-icons/pi";
import { Badge, Flex } from "rizzui";
import { RowActions } from "../../components/table/row-actions";
import { staleReasonLabel, type TierStalenessRow } from "../../lib/services/catalog";
import { inventoryPath, skuPath, tierPath } from "../../lib/paths";

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const PERCENT = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1, signDisplay: "always" });
const fmt = (value: number | null) => (value === null ? "—" : MONEY.format(value));
/**
 * `movementPct` arrives already a percentage — `StalenessPolicy.Movement` sends 20 for a 20% move — so
 * it is printed with a "%" sign, never through `style: "percent"`, which multiplied it by 100 again.
 */
const fmtPct = (value: number | null) => (value === null ? "—" : `${PERCENT.format(value)}%`);

/**
 * Accessors make every data column sortable on the API's own figure (a server-paged report, so
 * `PagedTable` labels such a sort as within-this-page); money and movement columns right-align.
 *
 * The two flags — both the API's own (`critical`, `manuallySet`) — sit under the SKU, as the order
 * list's stock flags sit under the order number, rather than in a column empty on most rows: the
 * first thing read on a row is whether it is critical.
 */
/**
 * `mayOpenStock`: the SKU links to its stock screen, `inventory.read` — plain text for a role without it.
 * `mayReapply`: the row menu's first item updates this row's tier (`pricing.administer`) rather than
 * only filtering to it.
 */
export const tierStalenessColumns = (mayOpenStock: boolean, mayReapply = false, oneTier = false): ColumnDef<TierStalenessRow>[] => [
  {
    id: "actions",
    header: "",
    size: 60,
    // The row's next steps, where the reader is looking: update its tier, or open what made it stale.
    cell: ({ row }) => {
      const { tierId, tierName, skuId } = row.original;
      return (
        <RowActions
          name={`${row.original.skuCode} ${row.original.unitCode} on ${tierName}`}
          primary={{
            label: mayReapply ? `Update ${tierName}'s prices` : `Show ${tierName} only`,
            icon: mayReapply ? UpdateIcon : FilterIcon,
            to: `/reports/staleness?tierId=${tierId}`,
          }}
          more={[
            { label: "Open the SKU's base prices", icon: PriceIcon, to: `${skuPath(skuId)}/base-prices` },
            { label: `Open ${tierName}'s discount rules`, icon: RulesIcon, to: `${tierPath(tierId)}/rules` },
            { label: `Open ${tierName}'s stale prices`, icon: TierIcon, to: `${tierPath(tierId)}/prices?isStale=1` },
          ]}
        />
      );
    },
  },
  {
    id: "sku",
    accessorKey: "skuCode",
    header: "SKU",
    size: 300,
    cell: ({ row }) => (
      <Flex direction="col" align="start" gap="1">
        {mayOpenStock ? (
          <Link to={inventoryPath(row.original.skuId)} className="font-mono text-xs font-semibold hover:underline">
            {row.original.skuCode}
          </Link>
        ) : (
          <span className="font-mono text-xs font-semibold">{row.original.skuCode}</span>
        )}
        {row.original.critical ? (
          <Badge size="sm" variant="flat" color="danger" rounded="md" className="gap-1 whitespace-nowrap">
            <PiWarningDuotone aria-hidden className="size-3.5" /> Critical
          </Badge>
        ) : null}
        {row.original.manuallySet ? (
          <Badge size="sm" variant="flat" color="secondary" rounded="md" className="gap-1 whitespace-nowrap">
            <PiLockSimpleDuotone aria-hidden className="size-3.5" /> Manually set — excluded from re-apply
          </Badge>
        ) : null}
      </Flex>
    ),
  },
  // Filtered to one tier, the column would repeat its name on every row; the filter already says it.
  ...(oneTier ? [] : [{ id: "tierName", accessorKey: "tierName", header: "Tier", cell: ({ row }) => row.original.tierName } satisfies ColumnDef<TierStalenessRow>]),
  { id: "unitCode", accessorKey: "unitCode", header: "Unit", cell: ({ row }) => row.original.unitCode },
  // Headers say what each figure means to a reader, not its field name: "Current rate" was read as
  // "the new price" when it is what this tier's customers are charged until a re-apply is committed.
  { id: "rate", accessorKey: "rate", header: "Customers pay now", meta: { align: "right" }, cell: ({ row }) => <span className="font-semibold tabular-nums">{fmt(row.original.rate)}</span> },
  { id: "currentBaseRate", accessorKey: "currentBaseRate", sortUndefined: "last", header: "Base price today", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{fmt(row.original.currentBaseRate)}</span> },
  { id: "movementValue", accessorKey: "movementValue", sortUndefined: "last", header: "Base price moved by", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{fmt(row.original.movementValue)}</span> },
  { id: "movementPct", accessorKey: "movementPct", sortUndefined: "last", header: "Moved %", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{fmtPct(row.original.movementPct)}</span> },
  { id: "reason", accessorFn: (row) => staleReasonLabel(row.reason), header: "Reason", size: 200, cell: ({ row }) => staleReasonLabel(row.original.reason) },
];
